import { useState, useEffect, useRef } from 'react'
import { supabase } from '../supabaseClient'
import { generateTeamSheetPdf, loadTeamSheetFonts, loadLogoBytes } from '../utils/generateTeamSheet'
import { getClubLogo } from '../clubLogos'

function arrayBufferToBase64(buffer) {
  let binary = ''
  const bytes = new Uint8Array(buffer)
  const chunkSize = 0x8000
  for (let i = 0; i < bytes.length; i += chunkSize) {
    binary += String.fromCharCode.apply(null, bytes.subarray(i, i + chunkSize))
  }
  return btoa(binary)
}

export default function TeamSheetModal({ fixture, profile, onClose }) {
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [pdfUrl, setPdfUrl] = useState(null)
  const [pdfBytes, setPdfBytes] = useState(null)
  const [coaches, setCoaches] = useState([])
  const [selectedCoaches, setSelectedCoaches] = useState([])
  const [emailSelf, setEmailSelf] = useState(false)
  const [freeTextEmail, setFreeTextEmail] = useState('')
  const [emailState, setEmailState] = useState(null) // null | 'sending' | 'sent' | 'error'
  const [emailError, setEmailError] = useState('')
  const blobUrlRef = useRef(null)

  useEffect(() => {
    generateSheet()
    loadCoaches()
    return () => { if (blobUrlRef.current) URL.revokeObjectURL(blobUrlRef.current) }
  }, [])

  async function loadCoaches() {
    const { data } = await supabase
      .from('squad_members')
      .select('profiles(id, full_name, email)')
      .eq('squad_id', fixture.squad_id)
      .eq('approved', true)
    const list = (data || [])
      .map(m => m.profiles)
      .filter(p => p && p.email)
    setCoaches(list)
  }

  async function generateSheet() {
    setLoading(true)
    setError('')
    try {
      const { data: squadPlayers } = await supabase
        .from('players')
        .select('id, full_name, full_name_irish, date_of_birth, foireann_id, is_guest')
        .eq('squad_id', fixture.squad_id)
        .eq('is_active', true)

      const { data: lineupRows } = await supabase
        .from('lineups')
        .select('player_id, starting, position_override, match_number')
        .eq('fixture_id', fixture.id)

      const lineupMap = {}
      ;(lineupRows || []).forEach(l => { lineupMap[l.player_id] = l })

      const players = (squadPlayers || [])
        .map(p => {
          const lineup = lineupMap[p.id]
          return {
            full_name: p.full_name,
            full_name_irish: p.full_name_irish,
            date_of_birth: p.date_of_birth,
            foireann_id: p.foireann_id,
            is_guest: p.is_guest || false,
            selected: !!lineup,
            starting: !!lineup?.starting,
            jersey_number: lineup?.match_number || lineup?.position_override || null
          }
        })
        .filter(p => !p.is_guest || p.selected)
        .sort((a, b) => {
          if (a.starting && !b.starting) return -1
          if (!a.starting && b.starting) return 1
          const an = parseInt(a.jersey_number) || 999
          const bn = parseInt(b.jersey_number) || 999
          return an - bn
        })

      const { data: managers } = await supabase
        .from('squad_managers')
        .select('full_name, full_name_irish, foireann_id')
        .eq('squad_id', fixture.squad_id)
        .order('full_name')

      const { data: clubData } = await supabase
        .from('clubs')
        .select('club_name, name, chairman, secretary')
        .eq('id', profile.club_id)
        .maybeSingle()

      const { fontRegular, fontBold } = await loadTeamSheetFonts()

      const clubLogoUrl = getClubLogo(clubData?.club_name || clubData?.name)
      let clubLogoBytes = null, clubLogoType = 'png'
      if (clubLogoUrl && !clubLogoUrl.includes('gaa-logo.png')) {
        const logoResult = await loadLogoBytes(clubLogoUrl)
        if (logoResult) { clubLogoBytes = logoResult.bytes; clubLogoType = logoResult.type }
      }
      const gaaLogoResult = await loadLogoBytes('/gaa-logo.png')

      const gameTypeLabels = { league: 'League', championship: 'Championship', cup: 'Cup', friendly: 'Friendly' }
      const dateObj = fixture.fixture_date ? new Date(fixture.fixture_date) : null
      const dateStr = dateObj ? dateObj.toLocaleDateString('en-IE', { day: '2-digit', month: '2-digit', year: 'numeric', timeZone: 'UTC' }) : ''

      const bytes = await generateTeamSheetPdf({
        club: clubData,
        squad: { name: fixture.squads?.name || '' },
        fixture: {
          opponent: fixture.opponent,
          competition: gameTypeLabels[fixture.game_type] || fixture.game_type,
          date: dateStr
        },
        players,
        managers: managers || [],
        fontRegular,
        fontBold,
        clubLogoBytes,
        clubLogoType,
        gaaLogoBytes: gaaLogoResult?.bytes || null,
        gaaLogoType: gaaLogoResult?.type || 'png'
      })

      setPdfBytes(bytes)
      const blob = new Blob([bytes], { type: 'application/pdf' })
      const url = URL.createObjectURL(blob)
      blobUrlRef.current = url
      setPdfUrl(url)
    } catch (e) {
      setError('Error generating team sheet: ' + e.message)
    }
    setLoading(false)
  }

  function downloadPdf() {
    if (!pdfUrl) return
    const a = document.createElement('a')
    a.href = pdfUrl
    a.download = `Team Sheet - ${fixture.squads?.name || 'Squad'} vs ${fixture.opponent}.pdf`
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
  }

  function toggleCoach(id) {
    setSelectedCoaches(prev => prev.includes(id) ? prev.filter(c => c !== id) : [...prev, id])
  }

  async function sendEmail() {
    const recipients = []
    coaches.filter(c => selectedCoaches.includes(c.id)).forEach(c => recipients.push(c.email))
    if (emailSelf && profile?.email) recipients.push(profile.email)
    const trimmedFreeText = freeTextEmail.trim()
    if (trimmedFreeText) recipients.push(trimmedFreeText)

    if (recipients.length === 0) {
      setEmailError('Select at least one recipient.')
      return
    }
    if (!pdfBytes) {
      setEmailError('Team sheet is still generating — please wait.')
      return
    }

    setEmailState('sending')
    setEmailError('')
    try {
      const pdfBase64 = arrayBufferToBase64(pdfBytes)
      const fileName = `Team Sheet - ${fixture.squads?.name || 'Squad'} vs ${fixture.opponent}.pdf`
      const subject = `Team Sheet: ${fixture.squads?.name || 'Squad'} vs ${fixture.opponent}`
      const htmlBody = `
        <div style="font-family: sans-serif; max-width: 480px; margin: 0 auto;">
          <div style="background: #185FA5; padding: 24px; border-radius: 12px 12px 0 0; text-align: center;">
            <h1 style="color: white; margin: 0; font-size: 20px;">Team Sheet</h1>
          </div>
          <div style="background: white; padding: 28px; border: 1px solid #e0e0e0; border-top: none; border-radius: 0 0 12px 12px;">
            <p style="color: #555; line-height: 1.6;"><strong>${fixture.squads?.name || 'Squad'}</strong> vs <strong>${fixture.opponent}</strong></p>
            <p style="color: #888; font-size: 13px;">Attached as a PDF — print and bring to the match.</p>
          </div>
        </div>`

      const res = await fetch('https://mrjtwgvscgcvuecxjcbg.supabase.co/functions/v1/send-invite-email', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': 'Bearer ' + process.env.REACT_APP_SUPABASE_ANON_KEY },
        body: JSON.stringify({
          type: 'team_sheet',
          toEmails: recipients,
          subject,
          htmlBody,
          pdfBase64,
          fileName
        })
      })
      if (!res.ok) {
        const data = await res.json().catch(() => ({}))
        throw new Error(data.error || 'Failed to send')
      }
      setEmailState('sent')
    } catch (e) {
      setEmailState('error')
      setEmailError(e.message)
    }
  }

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 1000, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', overflowY: 'auto', padding: '16px' }}
      onClick={e => { if (e.target === e.currentTarget) onClose() }}>
      <div style={{ background: '#f5f5f5', borderRadius: '16px', width: '100%', maxWidth: '560px', overflow: 'hidden', boxShadow: '0 8px 32px rgba(0,0,0,0.3)' }}>

        {/* Header */}
        <div style={{ background: 'linear-gradient(135deg, #0d3d6b 0%, #185FA5 100%)', color: 'white', padding: '18px 16px' }}>
          <p style={{ fontSize: '11px', opacity: 0.7, letterSpacing: '1px', marginBottom: '4px', textAlign: 'center' }}>TEAM SHEET</p>
          <p style={{ fontSize: '16px', fontWeight: '700', textAlign: 'center' }}>{fixture.squads?.name || 'Squad'} vs {fixture.opponent}</p>
        </div>

        {/* PDF preview */}
        <div style={{ background: 'white', margin: '12px 12px 0', borderRadius: '12px', overflow: 'hidden', boxShadow: '0 1px 4px rgba(0,0,0,0.08)' }}>
          {loading ? (
            <div style={{ padding: '60px 20px', textAlign: 'center', color: '#888', fontSize: '13px' }}>Generating team sheet...</div>
          ) : error ? (
            <div style={{ padding: '40px 20px', textAlign: 'center', color: '#A32D2D', fontSize: '13px' }}>{error}</div>
          ) : (
            <iframe title="Team sheet preview" src={pdfUrl} style={{ width: '100%', height: '420px', border: 'none', display: 'block' }} />
          )}
        </div>

        {!loading && !error && (
          <>
            {/* Download */}
            <div style={{ padding: '12px 12px 0' }}>
              <button onClick={downloadPdf}
                style={{ display: 'block', width: '100%', padding: '12px', background: '#E6F1FB', color: '#185FA5', border: 'none', borderRadius: '10px', fontSize: '13px', fontWeight: '500', cursor: 'pointer' }}>
                ⬇ Download PDF
              </button>
            </div>

            {/* Email section */}
            <div style={{ background: 'white', margin: '12px 12px 0', borderRadius: '12px', padding: '14px', boxShadow: '0 1px 4px rgba(0,0,0,0.08)' }}>
              <p style={{ fontSize: '11px', fontWeight: '600', color: '#888', letterSpacing: '0.5px', marginBottom: '10px' }}>EMAIL TEAM SHEET</p>

              {emailState === 'sent' ? (
                <p style={{ fontSize: '13px', color: '#3B6D11', textAlign: 'center', padding: '8px' }}>✅ Team sheet emailed successfully</p>
              ) : (
                <>
                  {coaches.length > 0 && (
                    <div style={{ marginBottom: '10px' }}>
                      <p style={{ fontSize: '11px', color: '#888', marginBottom: '6px' }}>Registered coaches</p>
                      {coaches.map(c => (
                        <label key={c.id} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '6px 0', fontSize: '13px', cursor: 'pointer' }}>
                          <input type="checkbox" checked={selectedCoaches.includes(c.id)} onChange={() => toggleCoach(c.id)} />
                          <span>{c.full_name} <span style={{ color: '#aaa', fontSize: '11px' }}>({c.email})</span></span>
                        </label>
                      ))}
                    </div>
                  )}

                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '6px 0', fontSize: '13px', cursor: 'pointer', borderTop: coaches.length > 0 ? '0.5px solid #f0f0f0' : 'none', marginTop: coaches.length > 0 ? '4px' : 0, paddingTop: coaches.length > 0 ? '10px' : '6px' }}>
                    <input type="checkbox" checked={emailSelf} onChange={e => setEmailSelf(e.target.checked)} />
                    <span>Email to myself <span style={{ color: '#aaa', fontSize: '11px' }}>({profile?.email})</span></span>
                  </label>

                  <div style={{ marginTop: '8px' }}>
                    <p style={{ fontSize: '11px', color: '#888', marginBottom: '4px' }}>Other email address (optional)</p>
                    <input type="email" placeholder="e.g. club secretary's email" value={freeTextEmail}
                      onChange={e => setFreeTextEmail(e.target.value)}
                      style={{ display: 'block', width: '100%', padding: '10px 12px', border: '1px solid #e0e0e0', borderRadius: '8px', fontSize: '13px', boxSizing: 'border-box' }} />
                  </div>

                  {emailError && (
                    <p style={{ fontSize: '12px', color: '#A32D2D', marginTop: '10px' }}>{emailError}</p>
                  )}

                  <button onClick={sendEmail} disabled={emailState === 'sending'}
                    style={{ display: 'block', width: '100%', padding: '11px', marginTop: '12px', background: '#185FA5', color: 'white', border: 'none', borderRadius: '8px', fontSize: '13px', fontWeight: '500', cursor: 'pointer', opacity: emailState === 'sending' ? 0.7 : 1 }}>
                    {emailState === 'sending' ? 'Sending...' : '📨 Send Team Sheet'}
                  </button>
                </>
              )}
            </div>
          </>
        )}

        {/* Close */}
        <div style={{ padding: '16px 12px' }}>
          <button onClick={onClose} style={{ width: '100%', padding: '12px', background: 'white', color: '#185FA5', border: '0.5px solid #185FA5', borderRadius: '10px', fontSize: '14px', fontWeight: '500', cursor: 'pointer' }}>
            Close
          </button>
        </div>
      </div>
    </div>
  )
}